import type React from 'react'
import { useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import styled from 'styled-components'
import { supabase } from '../../lib/supabase'
import { theme } from '../../theme/theme'
import type { CalculatedZone } from '../../types/zones'

interface PowerZonesChartProps {
  workoutId: string
  powerZones: CalculatedZone[]
}

interface ZoneWithStats extends CalculatedZone {
  timeInZone: number
  percentage: number
}

const Container = styled.div`
  width: 100%;
`

const PowerZonesChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`

const ZoneBar = styled.div<{ $percentage: number; $color: string }>`
  display: grid;
  grid-template-columns: 200px 1fr 100px;
  align-items: center;
  gap: ${theme.spacing.md};
`

const ZoneInfo = styled.div`
  min-width: 120px;
  font-size: ${theme.typography.sm};
`

const ZoneName = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`

const ZoneRange = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${theme.typography.xs};
`

const ZoneBarFill = styled.div<{ $percentage: number; $color: string }>`
  flex: 1;
  height: 20px;
  background: ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${({ $percentage }) => $percentage}%;
    background: ${({ $color }) => $color};
    transition: width 0.3s ease;
  }
`

const ZoneStats = styled.div`
  min-width: 80px;
  text-align: right;
  font-size: ${theme.typography.sm};
`

const ZoneTime = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`

const ZonePercentage = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${theme.typography.xs};
`

const LoadingMessage = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.sm};
`

const ErrorMessage = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${({ theme }) => theme.colors.error};
  font-size: ${theme.typography.sm};
`

const SkeletonWrapper = styled.div`
  .react-loading-skeleton {
    --base-color: ${({ theme }) => theme.colors.surface};
    --highlight-color: ${({ theme }) => theme.colors.border};
    border-radius: ${theme.borderRadius.md};
  }
`

const ChartSkeletonContainer = styled.div`
  width: 100%;
  padding: ${theme.spacing.md};
`

// Helper function to format time as mm:ss
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export const PowerZonesChart: React.FC<PowerZonesChartProps> = ({ workoutId, powerZones }) => {
  const [zonesWithStats, setZonesWithStats] = useState<ZoneWithStats[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadPowerData = async () => {
      if (!workoutId || !powerZones || powerZones.length === 0) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        // Fetch power data from the database
        const { data: powerData, error: powerError } = await supabase.rpc('get_workout_metrics', {
          p_workout_id: workoutId,
          p_metric_name: 'power',
        })

        if (powerError) throw powerError

        if (!powerData || powerData.length === 0) {
          setZonesWithStats([])
          setLoading(false)
          return
        }

        // Calculate time spent in each zone
        const zoneTotals: Record<string, number> = {}
        powerZones.forEach((zone) => {
          zoneTotals[zone.id] = 0
        })

        // Count samples in each zone (assuming 1 sample per second)
        powerData.forEach((sample) => {
          const power = sample.value
          for (const zone of powerZones) {
            if ((!zone.minValue || power >= zone.minValue) && (!zone.maxValue || power < zone.maxValue)) {
              zoneTotals[zone.id] += 1
              break
            }
          }
        })

        // Calculate total time for percentage calculation
        const totalTime = Object.values(zoneTotals).reduce((sum, time) => sum + time, 0)

        // Create zones with calculated stats
        const zonesWithCalculatedStats: ZoneWithStats[] = powerZones.map((zone) => ({
          ...zone,
          timeInZone: zoneTotals[zone.id] || 0,
          percentage: totalTime > 0 ? (zoneTotals[zone.id] / totalTime) * 100 : 0,
        }))

        setZonesWithStats(zonesWithCalculatedStats)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load power data')
        console.error('Error loading power data:', err)
      } finally {
        setLoading(false)
      }
    }

    loadPowerData()
  }, [workoutId, powerZones])

  if (loading) {
    return (
      <Container>
        <ChartSkeletonContainer>
          <SkeletonWrapper>
            <Skeleton height={200} count={5} style={{ marginBottom: 8 }} />
          </SkeletonWrapper>
        </ChartSkeletonContainer>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>Error: {error}</ErrorMessage>
      </Container>
    )
  }

  if (zonesWithStats.length === 0) {
    return (
      <Container>
        <LoadingMessage>No power zone data available for this workout.</LoadingMessage>
      </Container>
    )
  }

  return (
    <Container>
      <PowerZonesChartContainer>
        {zonesWithStats.map((zone) => (
          <ZoneBar key={zone.id} $percentage={zone.percentage} $color={zone.color}>
            <ZoneInfo>
              <ZoneName>Zone {zone.name}</ZoneName>
              <ZoneRange>
                {zone.minValue} - {zone.maxValue} W
              </ZoneRange>
            </ZoneInfo>
            <ZoneBarFill $percentage={zone.percentage} $color={zone.color} />
            <ZoneStats>
              <ZoneTime>{formatTime(zone.timeInZone)}</ZoneTime>
              <ZonePercentage>{zone.percentage.toFixed(0)}%</ZonePercentage>
            </ZoneStats>
          </ZoneBar>
        ))}
      </PowerZonesChartContainer>
    </Container>
  )
}
